'use client';

import { useToggleSeriesWatchlist } from '@/api/mutations/series.mutations';
import { useAuthStore } from '@/shared/stores/auth-store';
import type { Series } from '@/types';
import { useCallback, useState } from 'react';

type WatchlistToggleProps =
  { isBookmarked: boolean; onToggleBookmark: () => void } | Record<string, never>;

/**
 * Save/unsave props for series and course cards (FRONT-495). `/series-watchlist/{uuid}/toggle`
 * flips server state blindly, so the control only renders when the payload says whether the
 * item is saved — a card that guessed "not saved" would remove a saved item on click. Lists
 * built on `SeriesResource` / `CoursesResource` carry `in_watchlist` for signed-in callers.
 */
export function useSeriesWatchlistToggle(
  item: Series,
  type: 'series' | 'course'
): WatchlistToggleProps {
  const isAuthenticated = useAuthStore((s) => !!s.user);
  const { mutate } = useToggleSeriesWatchlist();
  const [override, setOverride] = useState<boolean | undefined>(undefined);

  const known = item.in_watchlist;
  const saved = override ?? known ?? false;

  const onToggleBookmark = useCallback((): void => {
    if (item.uuid === undefined) return;
    setOverride(!saved);
    mutate(
      { uuid: item.uuid, type },
      {
        onSuccess: (response) => setOverride(response.in_watchlist),
        onError: () => setOverride(saved),
      }
    );
  }, [item.uuid, type, saved, mutate]);

  if (!isAuthenticated || typeof known !== 'boolean' || item.uuid === undefined) return {};
  return { isBookmarked: saved, onToggleBookmark };
}
